/**
 * Universal Embed URL Transformer & Sanitizer
 * Automatically converts watch/share URLs from YouTube, Vimeo, Google Slides, etc.
 * into embed-compatible URLs that bypass X-Frame-Options: SAMEORIGIN errors.
 */

export interface EmbedUrlDetails {
  originalUrl: string;
  embedUrl: string;
  isConverted: boolean;
  provider: 'youtube' | 'vimeo' | 'googleslides' | 'generic';
  isValid: boolean;
  isBlockedRoot?: boolean;
  errorMessage?: string;
  videoId?: string;
}

/**
 * Robustly extracts YouTube Video ID or Playlist ID from any link format.
 * Supports:
 *  - https://www.youtube.com/watch?v=ID (any query params before or after)
 *  - https://m.youtube.com/watch?v=ID
 *  - https://youtube.com/watch?v=ID
 *  - https://youtu.be/ID
 *  - https://www.youtube.com/shorts/ID
 *  - https://www.youtube.com/live/ID
 *  - https://www.youtube.com/embed/ID
 *  - https://www.youtube-nocookie.com/embed/ID
 *  - https://www.youtube.com/v/ID
 *  - https://www.youtube.com/playlist?list=ID
 *  - Raw 11-char ID: ID
 *  - Extracted src from pasted <iframe src="...">
 */
export function extractYouTubeInfo(rawUrl: string): { type: 'video' | 'playlist'; id: string; listId?: string; startParam?: string } | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  let url = rawUrl.trim();

  // Strip iframe src
  const iframeSrcMatch = url.match(/src=["']([^"']+)["']/i);
  if (iframeSrcMatch) url = iframeSrcMatch[1].trim();

  // Strip markdown link
  const mdMatch = url.match(/\[.*?\]\((https?:\/\/[^\s)]+)\)/);
  if (mdMatch) url = mdMatch[1].trim();

  // 1. Standalone 11-char video ID (e.g. sO2g0Uv-L2g, dQw4w9WgXcQ)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return { type: 'video', id: url };
  }

  // Check if it's a YouTube domain or URL
  const isYt = /(?:youtube\.com|youtube-nocookie\.com|youtu\.be)/i.test(url);
  if (!isYt) return null;

  const startParam = extractYouTubeStartParam(url);

  // Playlist list param
  const listMatch = url.match(/[?&]list=([a-zA-Z0-9_-]+)/i);
  const listId = listMatch ? listMatch[1] : undefined;

  // 2. Watch URL with ?v=ID or &v=ID
  const vMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})(?:[&?]|$)/i);
  if (vMatch) {
    return { type: 'video', id: vMatch[1], listId, startParam };
  }

  // 3. youtu.be/ID
  const shortMatch = url.match(/(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})(?:[?&]|$)/i);
  if (shortMatch) {
    return { type: 'video', id: shortMatch[1], listId, startParam };
  }

  // 4. /shorts/ID or /live/ID or /embed/ID or /v/ID
  const pathMatch = url.match(/(?:https?:\/\/)?(?:[a-zA-Z0-9_-]+\.)?(?:youtube\.com|youtube-nocookie\.com)\/(?:shorts|live|embed|v)\/([a-zA-Z0-9_-]{11})(?:[?&]|$)/i);
  if (pathMatch) {
    return { type: 'video', id: pathMatch[1], listId, startParam };
  }

  // 5. Playlist only
  if (listId) {
    return { type: 'playlist', id: listId };
  }

  return null;
}

/**
 * Checks if the URL points to the YouTube domain (homepage, channel, or video).
 */
export function isYouTubeDomain(rawUrl: string): boolean {
  if (!rawUrl || typeof rawUrl !== 'string') return false;
  const clean = rawUrl.trim();
  return /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com|youtube-nocookie\.com|youtu\.be)/i.test(clean);
}

/**
 * Transforms any standard video or resource link into a frameable embed URL.
 * Specifically converts:
 *  - YouTube Watch / Shorts / Live / Share -> https://www.youtube-nocookie.com/embed/ID
 *  - YouTube Playlist -> https://www.youtube-nocookie.com/embed/videoseries?list=ID
 *  - Vimeo -> https://player.vimeo.com/video/ID
 *  - Google Slides /edit -> /embed
 *  - Extracts src from pasted <iframe src="..."> code
 *
 * NOTE: If a YouTube URL is passed without a video or playlist ID (e.g. https://www.youtube.com/),
 * this function returns an empty string to PREVENT rendering <iframe src="https://www.youtube.com/">
 * which would trigger browser error: "Refused to display ... because it set 'X-Frame-Options' to 'sameorigin'".
 */
export function getSafeEmbedUrl(rawUrl: string): string {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  let url = rawUrl.trim();

  // If user pasted an iframe tag into the URL field
  const iframeSrcMatch = url.match(/src=["']([^"']+)["']/i);
  if (iframeSrcMatch) {
    url = iframeSrcMatch[1].trim();
  }

  // Strip markdown link syntax: [Title](https://...)
  const mdMatch = url.match(/\[.*?\]\((https?:\/\/[^\s)]+)\)/);
  if (mdMatch) {
    url = mdMatch[1].trim();
  }

  try {
    // 1. YouTube video / playlist extraction
    if (isYouTubeDomain(url) || /^[a-zA-Z0-9_-]{11}$/.test(url)) {
      const ytInfo = extractYouTubeInfo(url);
      if (ytInfo) {
        if (ytInfo.type === 'video') {
          const listQuery = ytInfo.listId ? `&list=${encodeURIComponent(ytInfo.listId)}` : '';
          const startQuery = ytInfo.startParam || '';
          return `https://www.youtube-nocookie.com/embed/${ytInfo.id}?rel=0&enablejsapi=1${listQuery}${startQuery}`;
        }
        if (ytInfo.type === 'playlist') {
          return `https://www.youtube-nocookie.com/embed/videoseries?list=${encodeURIComponent(ytInfo.id)}&rel=0`;
        }
      }
      // If it's a YouTube domain but has NO video/playlist ID (e.g. https://www.youtube.com or https://youtube.com/)
      // DO NOT return the base URL because YouTube sets X-Frame-Options: SAMEORIGIN on its homepage!
      return '';
    }

    // 2. Vimeo: https://vimeo.com/123456789
    const vimeoMatch = url.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/i);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // 3. Google Slides: /edit -> /embed
    if (url.includes('docs.google.com/presentation') && url.includes('/edit')) {
      return url.replace(/\/edit.*$/, '/embed');
    }

    // 4. Google Docs / Sheets: /edit -> /preview
    if ((url.includes('docs.google.com/document') || url.includes('docs.google.com/spreadsheets')) && url.includes('/edit')) {
      return url.replace(/\/edit.*$/, '/preview');
    }
  } catch (err) {
    console.warn('[embedService] Failed parsing embed URL:', err);
  }

  return url;
}

/**
 * Helper to parse YouTube timestamp parameter (e.g. t=1m30s, t=90s, t=90, start=90)
 * and return query param formatted for YouTube embed (&start=90).
 */
function extractYouTubeStartParam(searchStr: string): string {
  if (!searchStr) return '';
  const match = searchStr.match(/[?&#](?:t|start)=([0-9hms]+)/i);
  if (!match) return '';

  const val = match[1];
  let seconds = 0;

  if (/^\d+$/.test(val)) {
    seconds = parseInt(val, 10);
  } else {
    const h = parseInt((val.match(/(\d+)h/i) || [])[1] || '0', 10);
    const m = parseInt((val.match(/(\d+)m/i) || [])[1] || '0', 10);
    const s = parseInt((val.match(/(\d+)s/i) || [])[1] || '0', 10);
    seconds = h * 3600 + m * 60 + s;
  }

  return seconds > 0 ? `&start=${seconds}` : '';
}

/**
 * Returns inspectable details about a URL conversion.
 */
export function inspectEmbedUrl(rawUrl: string): EmbedUrlDetails {
  const clean = (rawUrl || '').trim();
  const safe = getSafeEmbedUrl(clean);
  const isYt = isYouTubeDomain(clean) || /^[a-zA-Z0-9_-]{11}$/.test(clean);

  let provider: EmbedUrlDetails['provider'] = 'generic';
  if (isYt) provider = 'youtube';
  else if (clean.includes('vimeo.com')) provider = 'vimeo';
  else if (clean.includes('docs.google.com/presentation')) provider = 'googleslides';

  // Check if user entered a bare YouTube homepage or channel without video ID
  if (isYt && !safe) {
    return {
      originalUrl: clean,
      embedUrl: '',
      isConverted: false,
      provider: 'youtube',
      isValid: false,
      isBlockedRoot: true,
      errorMessage: "The base YouTube website (https://www.youtube.com/) blocks iframe embedding with 'X-Frame-Options: SAMEORIGIN'. Please provide a specific video link (e.g. https://www.youtube.com/watch?v=... or https://youtu.be/...)."
    };
  }

  const isValid = Boolean(safe && safe.length > 5);

  let videoId: string | undefined;
  if (provider === 'youtube') {
    const info = extractYouTubeInfo(clean);
    if (info?.type === 'video') videoId = info.id;
  }

  return {
    originalUrl: clean,
    embedUrl: safe,
    isConverted: clean !== '' && safe !== clean,
    provider,
    isValid,
    isBlockedRoot: false,
    videoId
  };
}

/**
 * Returns the recommended iframe sandbox attribute.
 * NOTE: For YouTube and Vimeo, omitting the sandbox attribute or returning undefined
 * matches YouTube's official embed specs and ensures full player compatibility
 * (captions, full-screen, resolution switching, audio).
 * For generic external sites, a restrictive sandbox is applied.
 */
export function getEmbedSandbox(url: string): string | undefined {
  if (!url) return undefined;
  const isVideo = url.includes('youtube') || url.includes('youtu.be') || url.includes('vimeo');
  if (isVideo) {
    // Return undefined to omit sandbox for YouTube/Vimeo so all native controls & media codecs work flawlessly
    return undefined;
  }
  return 'allow-scripts allow-same-origin allow-popups allow-forms';
}

/**
 * Standard allow policy for iframe media features
 */
export const EMBED_ALLOW_POLICY = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

export interface VideoMetadata {
  title: string;
  authorName: string;
  authorUrl?: string;
  thumbnailUrl: string;
  provider: 'youtube' | 'vimeo' | 'generic';
  width?: number;
  height?: number;
  aspectRatio?: '16:9' | '4:3' | 'custom';
  html?: string;
  description?: string;
}

/**
 * Calculates recommended aspect ratio based on oEmbed width and height.
 */
export function getRecommendedAspectRatio(width?: number, height?: number): '16:9' | '4:3' | 'custom' {
  if (!width || !height || height === 0) return '16:9';
  const ratio = width / height;
  if (Math.abs(ratio - 16 / 9) < 0.15) return '16:9';
  if (Math.abs(ratio - 4 / 3) < 0.15) return '4:3';
  return 'custom';
}

// In-memory LRU-like cache for video metadata to avoid redundant network queries
const videoMetaCache = new Map<string, VideoMetadata>();

/**
 * Fetches rich video metadata (title, author/channel, thumbnail, aspect ratio) via backend oEmbed proxy
 * with client-side fallback to noembed and direct YouTube thumbnail generation.
 */
export async function fetchVideoMetadata(rawUrl: string): Promise<VideoMetadata | null> {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const cleanUrl = rawUrl.trim();

  if (videoMetaCache.has(cleanUrl)) {
    return videoMetaCache.get(cleanUrl)!;
  }

  const ytInfo = extractYouTubeInfo(cleanUrl);
  const isYt = Boolean(ytInfo && ytInfo.type === 'video');

  // 1. Try local server proxy
  try {
    const proxyUrl = `/api/media/oembed?url=${encodeURIComponent(cleanUrl)}`;
    const response = await fetch(proxyUrl);
    if (response.ok) {
      const data = await response.json();
      if (data && data.title) {
        const result: VideoMetadata = {
          title: data.title,
          authorName: data.authorName || (isYt ? 'YouTube Creator' : 'Content Publisher'),
          authorUrl: data.authorUrl,
          thumbnailUrl: data.thumbnailUrl || (isYt && ytInfo ? `https://i.ytimg.com/vi/${ytInfo.id}/hqdefault.jpg` : ''),
          provider: data.provider || (isYt ? 'youtube' : 'generic'),
          width: data.width,
          height: data.height,
          aspectRatio: getRecommendedAspectRatio(data.width, data.height),
          html: data.html,
          description: data.description || ''
        };
        videoMetaCache.set(cleanUrl, result);
        return result;
      }
    }
  } catch (proxyErr) {
    // Proxy failed, fall through to client fallback
  }

  // 2. Client fallback via noembed.com (CORS enabled public aggregator)
  try {
    const noembedUrl = `https://noembed.com/embed?url=${encodeURIComponent(cleanUrl)}`;
    const noembedRes = await fetch(noembedUrl);
    if (noembedRes.ok) {
      const data = await noembedRes.json();
      if (data && data.title) {
        const result: VideoMetadata = {
          title: data.title,
          authorName: data.author_name || (isYt ? 'YouTube Creator' : 'Content Publisher'),
          authorUrl: data.author_url,
          thumbnailUrl: data.thumbnail_url || (isYt && ytInfo ? `https://i.ytimg.com/vi/${ytInfo.id}/hqdefault.jpg` : ''),
          provider: (data.provider_name || (isYt ? 'youtube' : 'generic')).toLowerCase() as any,
          width: data.width,
          height: data.height,
          aspectRatio: getRecommendedAspectRatio(data.width, data.height),
          html: data.html,
          description: data.description || ''
        };
        videoMetaCache.set(cleanUrl, result);
        return result;
      }
    }
  } catch (noembedErr) {
    // Fall through
  }

  // 3. Fallback for YouTube: construct high-res thumbnail and placeholder metadata
  if (isYt && ytInfo) {
    const result: VideoMetadata = {
      title: `Clinical Video: ${ytInfo.id}`,
      authorName: 'Clinical Faculty Demonstration',
      authorUrl: `https://www.youtube.com/watch?v=${ytInfo.id}`,
      thumbnailUrl: `https://i.ytimg.com/vi/${ytInfo.id}/hqdefault.jpg`,
      provider: 'youtube',
      aspectRatio: '16:9'
    };
    videoMetaCache.set(cleanUrl, result);
    return result;
  }

  return null;
}

/**
 * Returns deterministic high-resolution thumbnail URL for YouTube videos.
 */
export function getYouTubeThumbnail(videoId: string, quality: 'maxres' | 'hq' | 'mq' = 'hq'): string {
  if (!videoId) return '';
  const prefix = quality === 'maxres' ? 'maxresdefault.jpg' : (quality === 'mq' ? 'mqdefault.jpg' : 'hqdefault.jpg');
  return `https://i.ytimg.com/vi/${videoId}/${prefix}`;
}
